import type Database from 'better-sqlite3';
import type { RawSymbol } from '../../plugin-api/types.js';
import { extractHeritageEntries } from '../heritage.js';
import type { SymbolRow, SymbolWithFilePath } from '../types.js';

/**
 * Subclass/implementor lookup (TRA-2002). Served from the materialized
 * `symbol_heritage` side table — an indexed equality seek on `parent_name`.
 * Exported so the query-plan regression test pins this exact shape.
 */
export const FIND_IMPLEMENTORS_SQL = `SELECT DISTINCT s.*, f.path as file_path
        FROM symbol_heritage h
        JOIN symbols s ON s.id = h.symbol_id
        JOIN files f ON s.file_id = f.id
        WHERE h.parent_name = ?`;

export class SymbolRepository {
  private readonly _stmts: {
    insertSymbol: Database.Statement;
    bulkInsertSymbolNodesByFileId: Database.Statement;
    deleteSymbolsByFileId: Database.Statement;
    deleteSymbolNodesByFileId: Database.Statement;
    deleteHeritageBySymbolId: Database.Statement;
    insertHeritage: Database.Statement;
    getSymbolsByFileId: Database.Statement;
    getSymbolBySymbolId: Database.Statement;
    getSymbolByFqn: Database.Statement;
    getSymbolById: Database.Statement;
    getSymbolsBySymbolIdPrefix: Database.Statement;
    getSymbolsByName: Database.Statement;
  };

  constructor(private readonly db: Database.Database) {
    this._stmts = {
      // Atomic upsert that preserves the row id on symbol_id conflict.
      // The previous `INSERT OR REPLACE` was DELETE+INSERT under the hood,
      // which tripped `FOREIGN KEY constraint failed` whenever the replaced
      // symbol had children pointing at it via `parent_id` (NO ACTION
      // default refuses to orphan them on DELETE). See issue: hermes-agent
      // "Force-reindex after FK recovery still failed". `ON CONFLICT DO
      // UPDATE` keeps the id stable so every child FK + every node/edge
      // pointing at this symbol stays valid across reindex runs.
      insertSymbol: db.prepare(
        `INSERT INTO symbols (file_id, symbol_id, name, kind, fqn, parent_id, signature, byte_start, byte_end, line_start, line_end, metadata, cyclomatic, max_nesting, param_count)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
         ON CONFLICT(symbol_id) DO UPDATE SET
           file_id     = excluded.file_id,
           name        = excluded.name,
           kind        = excluded.kind,
           fqn         = excluded.fqn,
           parent_id   = excluded.parent_id,
           signature   = excluded.signature,
           byte_start  = excluded.byte_start,
           byte_end    = excluded.byte_end,
           line_start  = excluded.line_start,
           line_end    = excluded.line_end,
           metadata    = excluded.metadata,
           cyclomatic  = excluded.cyclomatic,
           max_nesting = excluded.max_nesting,
           param_count = excluded.param_count
         RETURNING id`,
      ),
      // Bulk graph-node creation for a file's symbols in a single statement.
      // Mirrors the exact rows produced by per-symbol createNode('symbol', id):
      // node_type='symbol', ref_id=symbols.id. INSERT OR IGNORE honors
      // UNIQUE(node_type, ref_id) so re-indexing is idempotent.
      bulkInsertSymbolNodesByFileId: db.prepare(
        `INSERT OR IGNORE INTO nodes (node_type, ref_id)
         SELECT 'symbol', id FROM symbols WHERE file_id = ?`,
      ),
      deleteSymbolsByFileId: db.prepare('DELETE FROM symbols WHERE file_id = ?'),
      deleteSymbolNodesByFileId: db.prepare(
        `DELETE FROM nodes WHERE node_type = 'symbol'
         AND ref_id IN (SELECT id FROM symbols WHERE file_id = ?)`,
      ),
      // TRA-2002: materialized heritage rows for this symbol are refreshed on
      // every write (delete + re-insert). Rows of deleted symbols vanish via
      // the ON DELETE CASCADE FK on symbol_heritage.symbol_id.
      deleteHeritageBySymbolId: db.prepare('DELETE FROM symbol_heritage WHERE symbol_id = ?'),
      insertHeritage: db.prepare(
        `INSERT OR IGNORE INTO symbol_heritage (symbol_id, parent_name, kind)
         VALUES (?, ?, ?)`,
      ),
      getSymbolsByFileId: db.prepare('SELECT * FROM symbols WHERE file_id = ? ORDER BY byte_start'),
      getSymbolBySymbolId: db.prepare('SELECT * FROM symbols WHERE symbol_id = ?'),
      getSymbolByFqn: db.prepare('SELECT * FROM symbols WHERE fqn = ?'),
      getSymbolById: db.prepare('SELECT * FROM symbols WHERE id = ?'),
      getSymbolsBySymbolIdPrefix: db.prepare(
        `SELECT * FROM symbols WHERE symbol_id LIKE ? ESCAPE '\\' ORDER BY symbol_id LIMIT ?`,
      ),
      getSymbolsByName: db.prepare(
        'SELECT * FROM symbols WHERE name = ? ORDER BY symbol_id LIMIT ?',
      ),
    };
  }

  insertSymbol(
    fileId: number,
    sym: RawSymbol,
    parentIdOverride: number | null | undefined,
    createNode: (nodeType: string, refId: number) => number,
  ): number {
    const parentId =
      parentIdOverride !== undefined
        ? parentIdOverride
        : sym.parentSymbolId
          ? ((
              this.db
                .prepare('SELECT id FROM symbols WHERE symbol_id = ?')
                .get(sym.parentSymbolId) as { id: number } | undefined
            )?.id ?? null)
          : null;

    const cyclomatic =
      ((sym.metadata as Record<string, unknown> | undefined)?.cyclomatic as number | undefined) ??
      null;
    const maxNesting =
      ((sym.metadata as Record<string, unknown> | undefined)?.max_nesting as number | undefined) ??
      null;
    const paramCount =
      ((sym.metadata as Record<string, unknown> | undefined)?.param_count as number | undefined) ??
      null;

    // Guard: auto-generate symbolId if missing (framework plugins may omit it)
    const symbolIdStr = sym.symbolId || `file:${fileId}::${sym.name}#${sym.kind}`;

    // `.get()` + RETURNING — UPDATE branches of ON CONFLICT do not populate
    // lastInsertRowid, so we ask SQLite for the resulting row id explicitly.
    const row = this._stmts.insertSymbol.get(
      fileId,
      symbolIdStr,
      sym.name,
      sym.kind,
      sym.fqn ?? null,
      parentId,
      sym.signature ?? null,
      sym.byteStart ?? 0,
      sym.byteEnd ?? 0,
      sym.lineStart ?? null,
      sym.lineEnd ?? null,
      sym.metadata ? JSON.stringify(sym.metadata) : null,
      cyclomatic,
      maxNesting,
      paramCount,
    ) as { id: number } | undefined;

    if (!row) {
      // Should never happen — RETURNING id always emits a row on both the
      // INSERT and ON CONFLICT DO UPDATE branches. If it does (e.g. a future
      // schema migration adds a constraint that's silently no-op'd), fail
      // loudly rather than return NaN and propagate corruption downstream.
      throw new Error(`insertSymbol: RETURNING produced no row for symbol_id=${symbolIdStr}`);
    }
    const symbolId = row.id;
    createNode('symbol', symbolId);
    this.syncHeritage(symbolId, sym.metadata);
    return symbolId;
  }

  /**
   * Refresh the materialized `symbol_heritage` rows for one symbol (TRA-2002).
   * The delete is unconditional so an upsert that *removes* heritage (same
   * symbol_id re-indexed without it) cannot leave stale parent rows behind;
   * it is a single indexed seek and a no-op for the common heritage-less
   * symbol. `sym.metadata` is the pre-stringify object, so extraction costs
   * no extra JSON parse.
   */
  private syncHeritage(symbolId: number, metadata: unknown): void {
    this._stmts.deleteHeritageBySymbolId.run(symbolId);
    for (const e of extractHeritageEntries(metadata)) {
      this._stmts.insertHeritage.run(symbolId, e.parentName, e.kind);
    }
  }

  insertSymbols(
    fileId: number,
    symbols: RawSymbol[],
    insertSymbolFn: (fileId: number, sym: RawSymbol, parentIdOverride?: number | null) => number,
  ): number[] {
    return this.db.transaction(() => {
      const parentSymbolIds = symbols
        .map((s) => s.parentSymbolId)
        .filter((id): id is string => id != null);

      const parentIdMap = new Map<string, number>();
      if (parentSymbolIds.length > 0) {
        const unique = [...new Set(parentSymbolIds)];
        // TRA-1005: chunk (same SQLITE_MAX_VARIABLE_NUMBER / V8 ceiling).
        const CHUNK = 900;
        for (let i = 0; i < unique.length; i += CHUNK) {
          const chunk = unique.slice(i, i + CHUNK);
          const placeholders = chunk.map(() => '?').join(',');
          const rows = this.db
            .prepare(`SELECT symbol_id, id FROM symbols WHERE symbol_id IN (${placeholders})`)
            .all(...chunk) as { symbol_id: string; id: number }[];
          for (const row of rows) parentIdMap.set(row.symbol_id, row.id);
        }
      }

      const ids: number[] = [];
      for (const sym of symbols) {
        let parentId: number | null = null;
        if (sym.parentSymbolId) {
          parentId = parentIdMap.get(sym.parentSymbolId) ?? null;
          if (parentId == null) {
            const idx = symbols.findIndex((s) => s.symbolId === sym.parentSymbolId);
            if (idx >= 0 && idx < ids.length) {
              parentId = ids[idx];
            }
          }
        }
        const id = insertSymbolFn(fileId, sym, parentId);
        ids.push(id);
        if (sym.symbolId) parentIdMap.set(sym.symbolId, id);
      }
      // Create all graph nodes for this file's symbols in ONE statement, after
      // every symbol row has landed (so the SELECT sees them) and still inside
      // this transaction. `insertSymbolFn` on the batch path deliberately skips
      // per-symbol createNode; this replaces it with a single bulk insert.
      // insertSymbols is always called per-file (single fileId) — verified at
      // every call site — so scoping by file_id captures exactly this batch's
      // symbols and no others.
      this.bulkCreateSymbolNodes(fileId);
      return ids;
    })();
  }

  /**
   * Bulk-create graph `nodes` rows for every symbol of a file in ONE statement,
   * instead of one INSERT-OR-IGNORE + one SELECT per symbol via createNode.
   *
   * Produces exactly the same rows the per-symbol `createNode('symbol', id)`
   * did: node_type='symbol', ref_id=symbols.id. `INSERT OR IGNORE` respects the
   * UNIQUE(node_type, ref_id) constraint, so re-indexing stays idempotent.
   *
   * MUST run after all symbol rows for `fileId` are inserted (so the SELECT sees
   * them) and — on the batch path — within the same transaction as the inserts.
   */
  bulkCreateSymbolNodes(fileId: number): void {
    this._stmts.bulkInsertSymbolNodesByFileId.run(fileId);
  }

  deleteSymbolsByFile(fileId: number): void {
    this._stmts.deleteSymbolNodesByFileId.run(fileId);
    this._stmts.deleteSymbolsByFileId.run(fileId);
  }

  getSymbolsByFile(fileId: number): SymbolRow[] {
    return this._stmts.getSymbolsByFileId.all(fileId) as SymbolRow[];
  }

  getSymbolBySymbolId(symbolId: string): SymbolRow | undefined {
    return this._stmts.getSymbolBySymbolId.get(symbolId) as SymbolRow | undefined;
  }

  getSymbolByFqn(fqn: string): SymbolRow | undefined {
    return this._stmts.getSymbolByFqn.get(fqn) as SymbolRow | undefined;
  }

  getSymbolById(id: number): SymbolRow | undefined {
    return this._stmts.getSymbolById.get(id) as SymbolRow | undefined;
  }

  /**
   * All symbols whose id starts with `prefix` + `#` (TRA-1660: the caller
   * guessed `file::Name` without the `#kind` suffix). LIKE metacharacters in
   * the prefix are escaped so they match literally. Ordered for deterministic
   * candidates; capped — callers only need to tell 1 apart from many.
   */
  findSymbolsBySymbolIdPrefix(prefix: string, limit = 6): SymbolRow[] {
    const escaped = prefix.replace(/([%_\\])/g, '\\$1');
    return this._stmts.getSymbolsBySymbolIdPrefix.all(`${escaped}#%`, limit) as SymbolRow[];
  }

  /** Every symbol with this exact simple name, ordered deterministically. */
  findSymbolsByName(name: string, limit = 6): SymbolRow[] {
    return this._stmts.getSymbolsByName.all(name, limit) as SymbolRow[];
  }

  getSymbolChildren(parentId: number): SymbolRow[] {
    return this.db
      .prepare('SELECT * FROM symbols WHERE parent_id = ?')
      .all(parentId) as SymbolRow[];
  }

  getSymbolByName(name: string, kind?: string): SymbolRow | undefined {
    if (kind) {
      return this.db
        .prepare('SELECT * FROM symbols WHERE name = ? AND kind = ? LIMIT 1')
        .get(name, kind) as SymbolRow | undefined;
    }
    return this.db.prepare('SELECT * FROM symbols WHERE name = ? LIMIT 1').get(name) as
      | SymbolRow
      | undefined;
  }

  getExportedSymbols(filePattern?: string): SymbolWithFilePath[] {
    if (filePattern) {
      const likePattern = filePattern.replace(/\*/g, '%').replace(/\?/g, '_');
      return this.db
        .prepare(
          `SELECT s.*, f.path as file_path
         FROM symbols s
         JOIN files f ON s.file_id = f.id
         WHERE json_extract(s.metadata, '$.exported') = 1
         AND f.path LIKE ?`,
        )
        .all(likePattern) as SymbolWithFilePath[];
    }
    return this.db
      .prepare(
        `SELECT s.*, f.path as file_path
       FROM symbols s
       JOIN files f ON s.file_id = f.id
       WHERE json_extract(s.metadata, '$.exported') = 1`,
      )
      .all() as SymbolWithFilePath[];
  }

  /**
   * Subclasses / implementors of `name`, served from the materialized
   * `symbol_heritage` side table (TRA-2002). The previous implementation ran
   * a leading-wildcard `LIKE '%"Foo"%'` over `json_extract(metadata, ...)`
   * on every symbol row — unindexable, 41s event-loop stalls in production.
   * This is an indexed equality seek on `parent_name`; heritage rows are
   * maintained at write time (see syncHeritage) and backfilled by migration
   * 34. DISTINCT guards the pathological `extends X implements X` double row.
   */
  findImplementors(name: string): SymbolWithFilePath[] {
    return this.db.prepare(FIND_IMPLEMENTORS_SQL).all(name) as SymbolWithFilePath[];
  }

  getSymbolsWithHeritage(fileIds?: number[]): (SymbolRow & { file_path: string })[] {
    const base = `SELECT s.*, f.path AS file_path
      FROM symbols s
      JOIN files f ON s.file_id = f.id
      WHERE s.metadata IS NOT NULL
        AND (json_extract(s.metadata, '$.extends') IS NOT NULL
          OR json_extract(s.metadata, '$.implements') IS NOT NULL)`;
    if (fileIds && fileIds.length > 0) {
      // TRA-1005: chunk — one `IN (?,...)` over the full array tops
      // SQLITE_MAX_VARIABLE_NUMBER (32766) past ~32k files, and spreading the
      // whole array into `.all(...ids)` trips V8's argument ceiling (~65k).
      const out: (SymbolRow & { file_path: string })[] = [];
      const CHUNK = 900;
      for (let i = 0; i < fileIds.length; i += CHUNK) {
        const chunk = fileIds.slice(i, i + CHUNK);
        const ph = chunk.map(() => '?').join(',');
        const rows = this.db
          .prepare(`${base} AND s.file_id IN (${ph})`)
          .all(...chunk) as (SymbolRow & {
          file_path: string;
        })[];
        for (const row of rows) out.push(row);
      }
      return out;
    }
    return this.db.prepare(base).all() as (SymbolRow & { file_path: string })[];
  }

  /**
   * All symbols for a set of files, in one chunked query per 900 files
   * instead of one query per file. TRA-651: the file-granularity graph build
   * called the per-file statement 1 507 times for a 1 817-file project —
   * 424 ms of the 150 ms/build was `prepare` + statement dispatch overhead,
   * not row work. Ordering is (file_id, byte_start) so a caller that grouped
   * by file still sees each file's symbols in source order.
   */
  getSymbolsByFileIds(fileIds: number[]): SymbolRow[] {
    if (fileIds.length === 0) return [];
    const out: SymbolRow[] = [];
    const CHUNK = 900;
    for (let i = 0; i < fileIds.length; i += CHUNK) {
      const chunk = fileIds.slice(i, i + CHUNK);
      const placeholders = chunk.map(() => '?').join(',');
      const rows = this.db
        .prepare(
          `SELECT * FROM symbols WHERE file_id IN (${placeholders}) ORDER BY file_id, byte_start`,
        )
        .all(...chunk) as SymbolRow[];
      // push(...rows) spreads through Function.apply and blows the stack at a
      // few hundred thousand symbols; a loop costs nothing and has no ceiling.
      for (const row of rows) out.push(row);
    }
    return out;
  }

  /**
   * The (id, file_id) pairs only. TRA-651: the file-level graph needs nothing
   * else from a symbol — it maps symbol ids to node ids and collapses the
   * resulting symbol→symbol edges back to files. Selecting `*` there loaded
   * every signature and metadata blob for every symbol in the project and
   * threw them away, which was the bulk of the ~45 MB of garbage each graph
   * request produced.
   */
  getSymbolFileRefsByFileIds(fileIds: number[]): { id: number; file_id: number }[] {
    if (fileIds.length === 0) return [];
    const out: { id: number; file_id: number }[] = [];
    const CHUNK = 900;
    for (let i = 0; i < fileIds.length; i += CHUNK) {
      const chunk = fileIds.slice(i, i + CHUNK);
      const placeholders = chunk.map(() => '?').join(',');
      const rows = this.db
        .prepare(`SELECT id, file_id FROM symbols WHERE file_id IN (${placeholders})`)
        .all(...chunk) as { id: number; file_id: number }[];
      for (const row of rows) out.push(row);
    }
    return out;
  }

  getSymbolsByIds(ids: number[]): Map<number, SymbolRow> {
    const map = new Map<number, SymbolRow>();
    if (ids.length === 0) return map;
    const CHUNK = 900;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK);
      const placeholders = chunk.map(() => '?').join(',');
      const rows = this.db
        .prepare(`SELECT * FROM symbols WHERE id IN (${placeholders})`)
        .all(...chunk) as SymbolRow[];
      for (const row of rows) map.set(row.id, row);
    }
    return map;
  }

  findSymbolByRole(name: string, frameworkRole?: string): SymbolRow | undefined {
    if (frameworkRole) {
      return this.db
        .prepare(
          `SELECT s.* FROM symbols s
         JOIN files f ON s.file_id = f.id
         WHERE f.framework_role = ?
           AND (s.name = ? OR s.fqn LIKE ?)
         LIMIT 1`,
        )
        .get(frameworkRole, name, `%\\${name}`) as SymbolRow | undefined;
    }
    return this.db
      .prepare('SELECT * FROM symbols WHERE name = ? AND kind = ? LIMIT 1')
      .get(name, 'class') as SymbolRow | undefined;
  }

  updateSymbolSummary(symbolId: number, summary: string): void {
    this.db.prepare('UPDATE symbols SET summary = ? WHERE id = ?').run(summary, symbolId);
  }

  countUnsummarizedSymbols(kinds: string[]): number {
    if (kinds.length === 0) return 0;
    const placeholders = kinds.map(() => '?').join(',');
    const row = this.db
      .prepare(`
      SELECT COUNT(*) as cnt FROM symbols s
      JOIN files f ON s.file_id = f.id
      WHERE s.summary IS NULL AND s.kind IN (${placeholders}) AND f.gitignored = 0
    `)
      .get(...kinds) as { cnt: number };
    return row.cnt;
  }

  countUnembeddedSymbols(): number {
    const row = this.db
      .prepare(`
      SELECT COUNT(*) as cnt FROM symbols s
      LEFT JOIN symbol_embeddings se ON se.symbol_id = s.id
      WHERE se.symbol_id IS NULL
    `)
      .get() as { cnt: number };
    return row.cnt;
  }

  getUnsummarizedSymbols(
    kinds: string[],
    limit: number,
  ): {
    id: number;
    name: string;
    fqn: string | null;
    kind: string;
    signature: string | null;
    file_path: string;
    byte_start: number;
    byte_end: number;
  }[] {
    if (kinds.length === 0) return [];
    const placeholders = kinds.map(() => '?').join(',');
    return this.db
      .prepare(`
      SELECT s.id, s.name, s.fqn, s.kind, s.signature, f.path as file_path, s.byte_start, s.byte_end
      FROM symbols s
      JOIN files f ON s.file_id = f.id
      WHERE s.summary IS NULL AND s.kind IN (${placeholders}) AND f.gitignored = 0
      LIMIT ?
    `)
      .all(...kinds, limit) as {
      id: number;
      name: string;
      fqn: string | null;
      kind: string;
      signature: string | null;
      file_path: string;
      byte_start: number;
      byte_end: number;
    }[];
  }
}
